//
// DOM Events
//

import { objectForEach } from '../object'
import { catchFunctionErrors } from '../error'
import { tagNameLower } from './info'

import options from '../options'

// Represent the known event types in a compact way, then at runtime transform it into a hash with event name as key (for fast lookup)
const knownEvents = {},
  knownEventTypesByEventName = {}

knownEvents['UIEvents'] = ['keyup', 'keydown', 'keypress']

knownEvents['MouseEvents'] = [
  'click',
  'dblclick',
  'mousedown',
  'mouseup',
  'mousemove',
  'mouseover',
  'mouseout',
  'mouseenter',
  'mouseleave'
]

objectForEach(knownEvents, function (eventType, knownEventsForType) {
  if (knownEventsForType.length) {
    for (let i = 0, j = knownEventsForType.length; i < j; i++) {
      knownEventTypesByEventName[knownEventsForType[i]] = eventType
    }
  }
})

function isClickOnCheckableElement(element: Element, eventType: string) {
  if (tagNameLower(element) !== 'input' || !(element as HTMLInputElement).type) return false
  if (eventType.toLowerCase() != 'click') return false
  const inputType = (element as HTMLInputElement).type
  return inputType == 'checkbox' || inputType == 'radio'
}

export function registerEventHandler(
  element: Element,
  eventType: string,
  handler: EventListener,
  eventOptions = false
): void {
  const wrappedHandler = catchFunctionErrors(handler)
  const mustUseNative = Boolean(eventOptions)
  const jQuery = options.jQuery

  if (!options.useOnlyNativeEvents && !mustUseNative && jQuery) {
    jQuery(element).on(eventType, wrappedHandler)
  } else if (typeof element.addEventListener === 'function') {
    element.addEventListener(eventType, wrappedHandler, eventOptions)
  } else {
    throw new Error("Browser doesn't support addEventListener")
  }
}

function hasClick(element: Element): element is Element & { click(): void } {
  return typeof (element as any).click === 'function'
}

export function triggerEvent(element: Element, eventType: string): void {
  if (!(element && element.nodeType)) {
    throw new Error('element must be a DOM node when calling triggerEvent')
  }

  // For click events on checkboxes and radio buttons, jQuery toggles the element checked state *after* the
  // event handler runs instead of *before*. (This was fixed in 1.9 for checkboxes but not for radio buttons.)
  const useClickWorkaround = isClickOnCheckableElement(element, eventType)

  if (!options.useOnlyNativeEvents && options.jQuery && !useClickWorkaround) {
    options.jQuery(element).trigger(eventType)
    return
  }

  if (typeof element.dispatchEvent !== 'function') {
    if (useClickWorkaround && hasClick(element)) {
      element.click()
      return
    }
    throw new Error("The supplied element doesn't support dispatchEvent")
  }

  const eventCategory = knownEventTypesByEventName[eventType] || 'HTMLEvents'
  const view = options.global as Window | undefined
  let event: Event
  if (eventCategory === 'MouseEvents' && typeof MouseEvent === 'function') {
    // Preserve the legacy initEvent(...) behavior of passing the element itself
    // as relatedTarget — handlers for mouseover/mouseout/mouseenter/mouseleave
    // observe event.relatedTarget.
    event = new MouseEvent(eventType, { bubbles: true, cancelable: true, view, relatedTarget: element })
  } else if (eventCategory === 'UIEvents' && typeof KeyboardEvent === 'function') {
    event = new KeyboardEvent(eventType, { bubbles: true, cancelable: true, view })
  } else {
    event = new Event(eventType, { bubbles: true, cancelable: true })
  }
  element.dispatchEvent(event)
}
